// The pricing engine's read on the SELECTED item, shown at the top of the 🧮 Price
// breakdown panel (spec 20260810). Readable `label · value` rows — the value carries
// the tone colour, and both label and value explain the metric on hover. This is the
// engine's CONCLUSION (demand, sell-speed, market, buy-verdict, risk); the SP→BP maths
// and the comps evidence sit below it.
//
// Renders nothing when the item has no signals (parked/unpriced, or priced before
// diagnostics logging) — no empty block.
//
// `rule` (the offer rule's answer, when the rule sets the buy price) adds what the offer
// was built from: the category and price band used, the points applied, the market cuts
// and the matrix version. `counts` are the sold and listed comps behind the demand tier.
//
// Also shown on Pricing Management for a market-price lookup, which carries the demand
// rows only (spec 20261007). It brings its own stylesheet for that reason.
import { ruleCategory, rulePointsLine, ruleCuts } from './offerRuleView.js';
import './quote-assessment.css';

// Identity confidence is the UPSTREAM signal, shown first: it is one half of the final
// confidence behind the Viable verdict and the sole gate on any buy-price uplift, so a
// weak identity caps everything downstream.
//
// The row shows TWO independent facets, because they answer different questions:
//   1. the ic_score BAND (Strong/Moderate/Weak) — a continuous 0–1 confidence, banded;
//      this carries the tone, so the colour always tracks how sure the engine is.
//   2. the PROVENANCE chip — HOW identity was established (photo-matched, repriced, …);
//      a muted chip beside the band, red only when it is a genuine problem.
// They were previously fused into one badge keyed on identity_status alone, which made
// 'Manual reprice · 81%' read as a contradiction (provenance where a score-band belonged)
// and let unmapped statuses ('conflicting', 'NONE') fall to a calm grey that hid a
// genuinely bad identity. Banding the tone off the score fixes both.
const IC_STRONG = 0.80;
const IC_MODERATE = 0.60;
function identityBand(score) {
    if (typeof score !== 'number') return { tone: 'badge-neutral', label: '—' };
    if (score >= IC_STRONG) return { tone: 'badge-success', label: 'Strong' };
    if (score >= IC_MODERATE) return { tone: 'badge-warning', label: 'Moderate' };
    return { tone: 'badge-error', label: 'Weak' };
}

// Provenance keyed on the lower-cased identity_status. `warn` marks a real problem — the
// photo disagrees with the declared model, or nothing matched — and tones the chip red;
// the rest are neutral context. An unmapped status still shows as a neutral chip (its raw
// value), so no provenance is ever silently dropped.
const IDENTITY_PROVENANCE = {
    confirmed: { label: 'photo-matched' },
    corrected: { label: 'photo-corrected' },
    conflicting: { label: 'photo conflict', warn: true },
    unconfirmed: { label: 'unverified' },
    manual_reprice: { label: 'repriced' },
    batch_item: { label: 'batch' },
    low: { label: 'weak match' },
    none: { label: 'no match', warn: true },
    ambiguous: { label: 'ambiguous' },
};
const IDENTITY_TIP = 'How sure the engine is it identified the exact item — model and variant — '
    + 'from the intake name, the photo match and any serial. The coloured band is that '
    + 'confidence: Strong ≥ 80%, Moderate ≥ 60%, Weak below. Identity is the upstream signal — '
    + 'one half of the final confidence behind the Viable verdict — so a weak identity caps '
    + 'everything downstream. The chip beside it shows how identity was established '
    + '(photo-matched, repriced, …). Clearer photos and a confirmed model lift it.';

export const DEMAND_TONE = { HIGH11PLUS: 'badge-success-strong', HIGH: 'badge-success', MEDIUM: 'badge-success', LOW: 'badge-warning', NONE: 'badge-neutral', UNKNOWN: 'badge-neutral' };
const SALE_TONE = { FAST: 'badge-success', MID: 'badge-info', SLOW: 'badge-warning', 'HIGH RISK': 'badge-error', NONE: 'badge-neutral' };
const VIABLE_TONE = { YES: 'badge-success', MARGINAL: 'badge-warning', NEEDS_REVIEW: 'badge-info', NO: 'badge-error', HIGH_RISK: 'badge-error' };
const STATE_TONE = { A: 'badge-success', B: 'badge-info', C: 'badge-neutral', D: 'badge-warning', E: 'badge-error', ACTIVE_ONLY: 'badge-neutral', UNKNOWN: 'badge-neutral' };
const STATE_LABEL = { A: 'Liquid & Supplied', B: 'Liquid & Scarce', C: 'Moderate', D: 'Thin / Long-tail', E: 'Oversupplied', ACTIVE_ONLY: 'Active only', UNKNOWN: 'Unknown' };

// Demand tiers that mean the item actually had sold comps — the only case where the
// price-spread (volatility) figure is meaningful. NONE/UNKNOWN items report 0, which is
// "couldn't compute", not "perfectly tight", so the spread row is suppressed for them.
const SOLD_DEMAND = new Set(['HIGH11PLUS', 'HIGH', 'MEDIUM', 'LOW']);
function spreadBucket(pct) {
    if (pct < 30) return { label: 'Tight', tone: 'badge-success' };
    if (pct <= 60) return { label: 'Moderate', tone: 'badge-neutral' };
    return { label: 'Scattered', tone: 'badge-warning' };
}

export const DEMAND_TIP ='Demand tier from the count of matching SOLD comps at pricing time — HIGH11PLUS ≥11, HIGH ≥5, MEDIUM ≥3, LOW ≥1, NONE 0, UNKNOWN when no sold data was found. Higher demand tracks higher sell-through.';
const SPREAD_TIP = 'How scattered the comparable SOLD prices are ((Q3−Q1)/median). Tight comps mean a clear market value and better sell-through; scattered comps are weaker. Secondary signal, shown only when there is sold data.';
const SALE_TIP = "The engine's composite sell-speed read, driven by market liquidity and supply: FAST → MID → SLOW → HIGH RISK (or NONE with too little data). Tracks realised 30-day sell-through most closely of any signal.";
const STATE_TIP = 'Combines how often it SELLS with how many are LISTED against you right now. '
    + 'A — sells well, healthy competition. '
    + 'B — sells well with almost nothing else listed: a strong seller\'s position. '
    + 'C — moderate on both. '
    + 'D — thin / niche, little sold data, slow. '
    + 'E — few sales but lots listed: oversupplied, slow to shift. '
    + 'Active only — priced off live listings, no sales data yet.';
const VIABLE_TIP = "The engine's buy verdict. YES — worth buying. MARGINAL — thin margin. "
    + "NO — not worth buying at the derived price (a bad market). "
    + "HIGH_RISK / NEEDS_REVIEW — NOT a bad buy: it may sell fine, but the engine isn't "
    + "confident enough about the item's identity or price to auto-approve, so a human "
    + "should check it (often weak photo match and/or scattered comp prices). Clearer "
    + "photos usually clear it.";

const spaced = (v) => String(v).replace(/_/g, ' ');

function Row({ label, tip, tone, children }) {
    return (
        <div className="qa-bp-row">
            <span className="qa-muted" title={tip}>{label}</span>
            <span className={`badge badge-sm ${tone || 'badge-neutral'}`} title={tip}>{children}</span>
        </div>
    );
}

const POINTS_TIP = 'The share of the resale price we offer. It starts from the base for this category and '
    + 'price band, then adds or takes points for the category, the demand tier and the condition.';
const CATEGORY_TIP = 'The category and price band the offer was built from. A note beside it means the '
    + 'sub-category has no entry of its own, so a wider one was used. Correct the category to change it.';

// The engine read squeezed into one table cell: demand, sell speed, market and viable as
// four small chips in the same tones as the rows below. Used by the verdict note table so
// the whole quote reads at a glance. The full label and the explanation sit on hover.
// Renders a dash when the item has no signals (unpriced, or the line is gone).
const MARKET_SHORT = { ACTIVE_ONLY: 'Active', UNKNOWN: '?' };
export function EngineChips({ signals }) {
    const { demand_tier, sale_probability, market_state, viable } = signals || {};
    const chips = [
        demand_tier && { key: 'demand', tone: DEMAND_TONE[demand_tier], text: demand_tier, tip: `Demand ${demand_tier}. ${DEMAND_TIP}` },
        sale_probability && { key: 'sale', tone: SALE_TONE[sale_probability], text: sale_probability, tip: `Sell speed ${sale_probability}. ${SALE_TIP}` },
        market_state && { key: 'state', tone: STATE_TONE[market_state], text: MARKET_SHORT[market_state] || market_state, tip: `Market ${STATE_LABEL[market_state] || spaced(market_state)}. ${STATE_TIP}` },
        viable && { key: 'viable', tone: VIABLE_TONE[viable], text: spaced(viable), tip: `Viable ${spaced(viable)}. ${VIABLE_TIP}` },
    ].filter(Boolean);
    if (chips.length === 0) return <span className="qa-muted">—</span>;
    return (
        <span className="qa-bp-riskchips">
            {chips.map(c => <span key={c.key} className={`badge badge-sm ${c.tone || 'badge-neutral'}`} title={c.tip}>{c.text}</span>)}
        </span>
    );
}

// The price-band uplift belonged to the old formula. With the offer rule there is no
// uplift to gate, so its flag would only mislead.
const isUpliftFlag = (flag) => /uplift/i.test(flag);

// `showZeroCounts`: also show the counts when both are zero. Quote Assessment hides them
// there; Pricing Management shows them, so its dialog reads the same as its table badge.
// A count that is null was not reported and is left out, never shown as 0.
export default function EngineReadBlock({ signals, rule = null, counts = null, showZeroCounts = false }) {
    if (!signals) return null;
    const { demand_tier, sale_probability, market_state, viable, volatility_pct, identity_status, ic_score } = signals;
    const flags = (signals.risk_flags || '').split(',').map(f => f.trim()).filter(Boolean)
        .filter(f => !(rule && isUpliftFlag(f)));
    const flagCount = flags.length;
    // Prefer the counts the oversupply rule compared (all listings, Google and overseas
    // included). Items priced before those were recorded fall back to the eBay + Reverb counts.
    const f = rule?.facts;
    const ruleCounts = f?.soldCount != null && f?.listedCount != null;
    const shown = ruleCounts ? { sold: f.soldCount, active: f.listedCount } : counts;
    const overseas = ruleCounts && f.listedUkCount != null ? f.listedCount - f.listedUkCount : 0;
    const countParts = [];
    if (shown?.sold != null) countParts.push(`${shown.sold} sold`);
    if (shown?.active != null) countParts.push(`${shown.active} listed${overseas > 0 ? ` (${f.listedUkCount} UK, ${overseas} overseas)` : ''}`);
    const anyCount = shown && (shown.sold > 0 || shown.active > 0);
    const compCounts = (anyCount || showZeroCounts) ? countParts.map(p => ` · ${p}`).join('') : '';

    const rows = [];
    // Identity leads — it is the upstream signal that caps everything below it. The band
    // (tone from the score) and the provenance chip (how identity was established) sit
    // side by side, right-aligned like the risk chips.
    if (identity_status || typeof ic_score === 'number') {
        const band = identityBand(typeof ic_score === 'number' ? ic_score : null);
        const pct = typeof ic_score === 'number' ? ` · ${Math.round(ic_score * 100)}%` : '';
        const key = String(identity_status || '').toLowerCase();
        const prov = identity_status ? (IDENTITY_PROVENANCE[key] || { label: spaced(key) }) : null;
        rows.push(
            <div className="qa-bp-row" key="identity">
                <span className="qa-muted" title={IDENTITY_TIP}>Identity</span>
                <span className="qa-bp-riskchips">
                    <span className={`badge badge-sm ${band.tone}`} title={IDENTITY_TIP}>{band.label}{pct}</span>
                    {prov && (
                        <span className={`qa-id-prov${prov.warn ? ' warn' : ''}`} title={IDENTITY_TIP}>
                            {prov.warn ? '⚠ ' : ''}{prov.label}
                        </span>
                    )}
                </span>
            </div>,
        );
    }
    if (demand_tier) rows.push(<Row key="demand" label="Demand" tone={DEMAND_TONE[demand_tier]} tip={DEMAND_TIP}>{demand_tier}{compCounts}</Row>);
    if (sale_probability) rows.push(<Row key="sale" label="Sell speed" tone={SALE_TONE[sale_probability]} tip={SALE_TIP}>{sale_probability}</Row>);
    if (market_state) rows.push(<Row key="state" label="Market" tone={STATE_TONE[market_state]} tip={STATE_TIP}>{market_state}{STATE_LABEL[market_state] ? ` · ${STATE_LABEL[market_state]}` : ''}</Row>);
    // Price spread: only when the item had sold comps AND a spread was computed (>0);
    // a 0 on a no-sold item is "couldn't compute", not "tight".
    if (SOLD_DEMAND.has(demand_tier) && typeof volatility_pct === 'number' && volatility_pct > 0) {
        const b = spreadBucket(volatility_pct);
        rows.push(<Row key="spread" label="Price spread" tone={b.tone} tip={SPREAD_TIP}>{b.label} · {Math.round(volatility_pct)}%</Row>);
    }
    if (viable) rows.push(<Row key="viable" label="Viable" tone={VIABLE_TONE[viable]} tip={VIABLE_TIP}>{spaced(viable)}</Row>);
    // One red chip per flag, wrapping side-by-side — clearer than a "2 flags" count.
    if (flagCount > 0) {
        rows.push(
            <div className="qa-bp-row" key="risk">
                <span className="qa-muted">Risk</span>
                <span className="qa-bp-riskchips">
                    {flags.map((f, i) => (
                        <span key={i} className="badge badge-sm badge-error"
                            title={`Risk warning the engine raised on this item: ${spaced(f)}`}>
                            ⚠ {spaced(f)}
                        </span>
                    ))}
                </span>
            </div>,
        );
    }

    if (rule) {
        const category = ruleCategory(rule);
        const cuts = ruleCuts(rule);
        rows.push(
            <div className="qa-bp-row" key="rule-category">
                <span className="qa-muted" title={CATEGORY_TIP}>Priced as</span>
                <span className="qa-bp-riskchips">
                    <span className="badge badge-sm badge-neutral" title={CATEGORY_TIP}>{category.label}</span>
                    {category.fallback && <span className="qa-id-prov" title={CATEGORY_TIP}>{category.fallback}</span>}
                </span>
            </div>,
        );
        rows.push(<Row key="rule-points" label="Offer %" tip={POINTS_TIP}>{rulePointsLine(rule)}</Row>);
        if (cuts.length > 0) {
            rows.push(
                <div className="qa-bp-row" key="rule-cuts">
                    <span className="qa-muted">Cuts</span>
                    <span className="qa-bp-riskchips">
                        {cuts.map(c => <span key={c.key} className="badge badge-sm badge-warning" title={c.tip}>{c.label}</span>)}
                    </span>
                </div>,
            );
        }
        rows.push(
            <div className="qa-bp-row" key="rule-version">
                <span className="qa-muted">Buying matrix</span><span className="qa-id-prov">{rule.matrixVersion}</span>
            </div>,
        );
    }

    if (rows.length === 0) return null;
    return (
        <div className="qa-bp-signals">
            <div className="qa-bp-subhead">Engine read</div>
            {rows}
        </div>
    );
}
